import {
  DataSourceWithExtras,
  Transformation,
} from '../services/common/common.types';
import { DataSourceType } from '../types/data-source.types';

export type SidebarLeafItem = {
  id: string;
  label: string;
  dataSourceId?: number;
  sourceType?: string;
  destinationType?: string;
  transformationId?: number;
  imageLink: string | null;
};

export type SidebarSubGroup = {
  groupId: string;
  groupLabel: string;
  items: SidebarLeafItem[];
};

export type SidebarSection = {
  id: string;
  label: string;
  icon: string;
  subGroups: SidebarSubGroup[];
};

const REST_API_TYPES = new Set<string>([
  DataSourceType.API,
  DataSourceType.REAL_TIME_SOURCE,
]);

const SOURCE_GROUP_ORDER: { groupId: string; label: string; types: Set<string> }[] = [
  {
    groupId: 'file-system',
    label: 'File System',
    types: new Set([DataSourceType.FILESYSTEM]),
  },
  {
    groupId: 'file',
    label: 'File',
    types: new Set([DataSourceType.FILE]),
  },
  {
    groupId: 'rdbms',
    label: 'RDBMS',
    types: new Set([DataSourceType.RDBMS]),
  },
  {
    groupId: 'rest-apis',
    label: 'Rest APIs',
    types: REST_API_TYPES,
  },
  {
    groupId: 'no-sql',
    label: 'No SQL',
    types: new Set([DataSourceType.NO_SQL]),
  },
];

const DESTINATION_GROUP_ORDER: { groupId: string; label: string; types: Set<string> }[] = [
  {
    groupId: 'file-system',
    label: 'File System',
    types: new Set([DataSourceType.FILESYSTEM]),
  },
  {
    groupId: 'file',
    label: 'File',
    types: new Set([DataSourceType.FILE]),
  },
  {
    groupId: 'rdbms',
    label: 'RDBMS',
    types: new Set([DataSourceType.RDBMS]),
  },
  {
    groupId: 'no-sql',
    label: 'No SQL',
    types: new Set([DataSourceType.NO_SQL]),
  },
];

function groupDataSources(
  sources: DataSourceWithExtras[],
  groupDefs: { groupId: string; label: string; types: Set<string> }[],
  type: 'source' | 'destination'
): SidebarSubGroup[] {
  const buckets = new Map<string, { def: typeof groupDefs[0]; items: SidebarLeafItem[] }>();

  for (const def of groupDefs) {
    buckets.set(def.groupId, { def, items: [] });
  }

  for (const source of sources) {
    const dataSourceType = source.data_source_type?.toUpperCase().trim();
    const matchedDef = groupDefs.find((def) =>
      [...def.types].some((t) => t.toUpperCase() === dataSourceType)
    );
    if (!matchedDef) continue;

    const bucket = buckets.get(matchedDef.groupId);
    bucket.items.push({
      id: `${matchedDef.groupId}-${source.data_source_id}`,
      label: source.data_source_name,
      dataSourceId: source.data_source_id,
      [type === 'source' ? 'sourceType' : 'destinationType']: source.data_source_type,
      imageLink: source.image_link ?? null,
    });
  }

  return groupDefs
    .map((def) => {
      const bucket = buckets.get(def.groupId);
      const sortedItems = [...bucket.items].sort((a, b) => a.label.localeCompare(b.label));
      return {
        groupId: def.groupId,
        groupLabel: def.label,
        items: sortedItems,
      };
    })
    .filter((g) => g.items.length > 0);
}

/**
 * Catalog abbreviations that always belong in the Integrations sidebar — used
 * as a fallback when the backend hasn't tagged `is_integration` yet.
 *
 * Note: Union (`UN`) is intentionally NOT here yet. The Join PR added the type
 * + priority sort, but Union has no builder in `transformationDefinition.utils`,
 * no integration sheet, and `IntegrationNode` hard-routes every settings click
 * to the Join sheet. Re-add `'UN'` once those gaps are closed.
 */
const INTEGRATION_ABBREVIATIONS = new Set<string>(['JR']);

function groupTransformations(transformations: Transformation[]): {
  integrations: SidebarLeafItem[];
  transformationsList: SidebarLeafItem[];
} {
  const integrations: SidebarLeafItem[] = [];
  const transformationsList: SidebarLeafItem[] = [];

  for (const t of transformations) {
    const leaf: SidebarLeafItem = {
      id: `transformation-${t.id}`,
      label: t.name,
      transformationId: t.id,
      imageLink: t.image_link ?? null,
    };

    const abbreviation = typeof t.abbreviation === 'string' ? t.abbreviation : '';
    const isIntegration =
      (t as any).is_integration === true || INTEGRATION_ABBREVIATIONS.has(abbreviation);

    if (isIntegration) {
      integrations.push(leaf);
    } else {
      transformationsList.push(leaf);
    }
  }

  const sortedIntegrations = [...integrations].sort((a, b) => a.label.localeCompare(b.label));
  const sortedTransformationsList = [...transformationsList].sort((a, b) => a.label.localeCompare(b.label));

  return {
    integrations: sortedIntegrations,
    transformationsList: sortedTransformationsList,
  };
}

export function buildSidebarSections(
  sourceDataSources: DataSourceWithExtras[],
  targetDataSources: DataSourceWithExtras[],
  transformations: Transformation[]
): SidebarSection[] {
  const sourceGroups = groupDataSources(sourceDataSources, SOURCE_GROUP_ORDER, 'source');
  const destinationGroups = groupDataSources(targetDataSources, DESTINATION_GROUP_ORDER, 'destination');
  const { integrations, transformationsList } = groupTransformations(transformations);

  const sections: SidebarSection[] = [
    {
      id: 'data-sources',
      label: 'Data Sources',
      icon: 'storage',
      subGroups: sourceGroups,
    },
    {
      id: 'integrations',
      label: 'Integrations',
      icon: 'extension',
      subGroups: integrations.length
        ? [
          {
            groupId: 'integrations-list',
            groupLabel: 'Integrations',
            items: integrations,
          },
        ]
        : [],
    },
    {
      id: 'transformations',
      label: 'Transformations',
      icon: 'transform',
      subGroups: transformationsList.length
        ? [
          {
            groupId: 'transformations-list',
            groupLabel: 'Transformations',
            items: transformationsList,
          },
        ]
        : [],
    },
    {
      id: 'data-destinations',
      label: 'Data Destinations',
      icon: 'cloud_upload',
      subGroups: destinationGroups,
    },
  ];

  return sections;
}
